/**
 * Shared presentation for an archived facility row — OR-1431.
 *
 * Andressa's spec: 40% opacity plus a strikethrough on the data. Every table
 * that lists facilities (Schedule, UW Information, Regulatory, Operational,
 * Legal) uses the same treatment, so the classes and the copy live here rather
 * than being retyped per page.
 */

/**
 * Row class for an archived facility.
 *
 * `pointer-events-none` is cosmetic only — the real "frozen until restored"
 * guarantee is enforced in `useFacilities.update`, so no keyboard path or
 * dropdown that ignores its read-only prop can write to an archived row.
 *
 * Applied to every cell rather than "all but the last": Acuity renders one
 * `<tr>` per care type with the identity and action cells `rowSpan`-ned from
 * the first, so `:last-child` is an actions cell on one row and a year cell on
 * the next three. Interactive children that must survive — the ⋮ menu and the
 * tooltip trigger — opt back in with `ARCHIVED_INTERACTIVE`.
 */
export const ARCHIVED_ROW = "opacity-40 [&_td]:line-through [&_td]:pointer-events-none";

/** Re-enables pointer events for a control inside an archived row. The ⋮ menu
 *  needs it — it's the only way back out of the archived state. */
export const ARCHIVED_INTERACTIVE = "pointer-events-auto";

/**
 * Visual-only variant: dimmed and struck through, but still interactive.
 *
 * For tables whose rows are NOT the facility — Loss Runs (a row is a claim) and
 * Operational (a row is a year's staffing). Their rows belong to an archived
 * facility, so they no longer count toward the totals and should read that way,
 * but the row's own data is still claim/staffing data the user can edit or
 * delete. `ARCHIVED_ROW` would take that away.
 *
 * Those tables do carry the ⋮ (QA, 4-aug: the spec asks for it everywhere), but
 * it means "delete this row" — see `row-actions-menu.tsx`. Archiving there
 * would need a per-row exclusion flag the backend doesn't have; `is_excluded`
 * lives only on `SubmissionLocation`.
 */
export const ARCHIVED_ROW_VISUAL = "opacity-40 [&_td]:line-through";

/**
 * Hover copy on an archived row, everywhere except Legal.
 *
 * The line breaks are literal, matching where Andressa's mockup breaks them —
 * atlas `Tooltip` renders its title with `whitespace-pre-line` precisely so
 * callers can pass multi-line strings. Left to reflow inside `max-w-xs` this
 * lands on two lines instead of the three in the design, and no single width
 * gives three lines for both this string and the longer Legal one.
 */
export const ARCHIVED_TOOLTIP =
    "This facility is archived and not included\nin the policy's coverage.\nRestore to bring it back";

/**
 * Legal gets its own copy: it's the one page that HIDES archived facilities
 * from the view (the claims map) instead of only striking them through, so the
 * tooltip has to say the marker is gone, not just that the row is inactive.
 * Same manual line breaks as above.
 */
export const ARCHIVED_TOOLTIP_LEGAL =
    "This facility is archived and not included\nin the policy's coverage. It won't appear\non the map until you restore it.";

/** Wrapper for a tooltip trigger inside an archived row: re-enables the
 *  pointer events `ARCHIVED_ROW` switches off, so the row can still explain
 *  itself on hover. Geometry matches an editable cell (`h-9`, `px-3`) so
 *  swapping a control for static text doesn't shift the row. */
export const ARCHIVED_TOOLTIP_TRIGGER =
    "pointer-events-auto flex h-9 w-full cursor-help items-center px-3 text-left";
